import { useQuery } from '@apollo/client';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { isValidGALink } from '../../utils/analytics';
import {
  ADMIN_PATHS,
  ANALYTICS_EVENTS,
  CCUS_NOTES_SUBSCRIPTION,
  CCUS_PATHS,
  CCUS_SUBSCRIPTION,
  CLM_PATHS, // Feature flag: enable_consolidated_live_models
  CLM_SUBSCRIPTION, // Feature flag: enable_consolidated_live_models
  GHG_PATHS,
  GHG_SCOPE_3_PATHS,
  GHG_SCOPE_3_SUBSCRIPTION,
  GHG_SUBSCRIPTION,
  INTELLIGENCE_PATHS,
  INTELLIGENCE_SUBSCRIPTION,
  M_A_NOTES_SUBSCRIPTION,
  M_A_OPPORTUNITIES_PATHS,
  M_A_OPPORTUNITIES_SUBSCRIPTION,
  M_A_TRANSACTIONS_PATHS,
  M_A_TRANSACTIONS_SUBSCRIPTION,
  OIL_AND_GAS_MAP_PATHS,
  OIL_AND_GAS_MAP_SUBSCRIPTION,
  PORTFOLIO_ANALYSIS_PATHS,
  PORTFOLIO_ANALYSIS_SUBSCRIPTION,
  PUBLIC_PATHS,
  WEB_BASIC_PATHS,
  WEB_BASIC_SUBSCRIPTION,
} from '../../utils/constants';
import { curateLocation } from '../../utils/helpers';
import { CURRENT_USER_QUERY } from './operations';

const CurrentUserContext = createContext();

export const CurrentUserProvider = props => {
  const history = useHistory();
  const [currentUser, setCurrentUser] = useState(null);

  const {
    data,
    previousData,
    loading,
    refetch: refreshProvider,
  } = useQuery(CURRENT_USER_QUERY, { fetchPolicy: 'network-only' });

  const clearCurrentUser = () => setCurrentUser(null);

  useEffect(() => {
    const currentUser = loading ? previousData?.currentUserProfile : data?.currentUserProfile;
    if (currentUser) {
      setCurrentUser(currentUser);
      gtag('set', { userId: currentUser.id });
    }
  }, [loading, data, previousData]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: currentUser?.id is intentionally excluded to avoid unnecessary re-renders
  useEffect(() => {
    // need to send if url typed directly (not on route change listener)
    // i.e. oil and gas map opens in new tab
    if (isValidGALink(location.pathname)) {
      gtag('event', ANALYTICS_EVENTS.pageview, {
        page: location.pathname,
        userId: currentUser?.id,
      });
    }
    // listen for route changes
    return history.listen(location => {
      if (isValidGALink(location.pathname)) {
        gtag('event', ANALYTICS_EVENTS.pageview, {
          page: location.pathname,
          userId: currentUser?.id,
        });
      }
    });
  }, [history]);

  const isPublicPath = location => location === '/' || PUBLIC_PATHS.includes(location);
  const isAdminsPath = location => ADMIN_PATHS.includes(location);
  const isAuthenticated = useMemo(() => !!currentUser?.id, [currentUser]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: isAdminsPath and isPublicPath are stable functions that don't need to be in dependencies
  const isUserEntitled = useCallback(
    location => {
      const featuresEntitled = currentUser?.featuresEntitled;

      if (!featuresEntitled) return false;
      if (isPublicPath(location)) return true;

      const curatedPath = curateLocation(location);
      if (isAdminsPath(curatedPath)) {
        return currentUser?.admin;
      }

      const featurePathsMap = {
        [WEB_BASIC_SUBSCRIPTION]: WEB_BASIC_PATHS,
        [OIL_AND_GAS_MAP_SUBSCRIPTION]: OIL_AND_GAS_MAP_PATHS,
        [CCUS_SUBSCRIPTION]: CCUS_PATHS,
        [M_A_TRANSACTIONS_SUBSCRIPTION]: M_A_TRANSACTIONS_PATHS,
        [M_A_OPPORTUNITIES_SUBSCRIPTION]: M_A_OPPORTUNITIES_PATHS,
        [INTELLIGENCE_SUBSCRIPTION]: INTELLIGENCE_PATHS,
        [CCUS_NOTES_SUBSCRIPTION]: INTELLIGENCE_PATHS,
        [M_A_NOTES_SUBSCRIPTION]: INTELLIGENCE_PATHS,
        [PORTFOLIO_ANALYSIS_SUBSCRIPTION]: PORTFOLIO_ANALYSIS_PATHS,
        [CLM_SUBSCRIPTION]: CLM_PATHS, // Feature flag: enable_consolidated_live_models
        [GHG_SUBSCRIPTION]: GHG_PATHS,
        [GHG_SCOPE_3_SUBSCRIPTION]: GHG_SCOPE_3_PATHS,
      };

      return Object.entries(featurePathsMap).some(([feature, paths]) => {
        return featuresEntitled?.includes(feature) && paths.includes(curatedPath);
      });
    },
    [currentUser],
  );

  return (
    <CurrentUserContext.Provider
      value={{
        currentUser,
        clearCurrentUser,
        loading,
        isUserEntitled,
        refreshProvider,
        isAuthenticated,
      }}
    >
      {props.children}
    </CurrentUserContext.Provider>
  );
};

export const CurrentUserConsumer = CurrentUserContext.Consumer;

export const useCurrentUser = () => useContext(CurrentUserContext);

export default CurrentUserContext;
